// =============================================================================
// MeuDriver — Q1: Hook de disponibilidade semanal recorrente do instrutor
// Tabela: instructor_weekly_availability
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

// ─── Tipos ───────────────────────────────────────────────────────────────────

export interface WeeklyAvailabilityRow {
  id: string
  instructor_id: string
  day_of_week: number   // 0 = domingo … 6 = sábado
  start_time: string    // "HH:MM:SS"
  end_time: string      // "HH:MM:SS"
  is_active: boolean
  created_at: string
}

export interface WeeklySlotInput {
  day_of_week: number
  start_time: string  // "HH:MM"
  end_time: string    // "HH:MM"
}

// ─── Chaves de query ─────────────────────────────────────────────────────────

const KEYS = {
  byInstructor: (id: string) => ['weekly_availability', id] as const,
}

// ─── useInstructorWeeklyAvailability ─────────────────────────────────────────

/**
 * Busca os slots de disponibilidade semanal de um instrutor.
 * instructorId é o ID da tabela instructors (não profile_id).
 * Se omitido, tenta usar o instrutor do usuário autenticado.
 */
export function useInstructorWeeklyAvailability(instructorId?: string) {
  return useQuery({
    queryKey: KEYS.byInstructor(instructorId ?? ''),
    enabled: Boolean(instructorId),
    queryFn: async (): Promise<WeeklyAvailabilityRow[]> => {
      const { data, error } = await supabase
        .from('instructor_weekly_availability')
        .select('*')
        .eq('instructor_id', instructorId!)
        .eq('is_active', true)
        .order('day_of_week')
        .order('start_time')

      if (error) throw new Error(error.message)
      return (data ?? []) as WeeklyAvailabilityRow[]
    },
    staleTime: 60_000,
  })
}

// ─── useSetWeeklyAvailability ─────────────────────────────────────────────────

/**
 * Substituição atômica da disponibilidade semanal via RPC set_weekly_availability.
 * Deleta todos os slots existentes e insere os novos em uma transação.
 */
export function useSetWeeklyAvailability() {
  const { user } = useAuth()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (slots: WeeklySlotInput[]) => {
      const { error } = await supabase.rpc('set_weekly_availability', {
        p_slots: JSON.stringify(slots),
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      // Invalida queries do instrutor autenticado (instructorId = resolvido pelo banco)
      if (user?.id) {
        // Invalidamos todas as entradas da chave base para cobrir o caso
        // em que não sabemos o instructor_id (uuid) aqui no front
        qc.invalidateQueries({ queryKey: ['weekly_availability'] })
      }
      toast.success('Disponibilidade semanal atualizada com sucesso!')
    },
    onError: (err: Error) => {
      toast.error(`Erro ao salvar disponibilidade: ${err.message}`)
    },
  })
}
